import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Eye, ShieldAlert } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/useAuthStore'
import { endSupportSession, getVisibleSupportSessions } from '../api'

const VISIBLE_KEY = ['support-sessions', 'visible'] as const

/** Minutes left, refreshed every half minute. Kept in state so render stays pure. */
function useMinutesLeft(expiresAt: string | undefined): number | null {
  const [left, setLeft] = useState<number | null>(null)
  useEffect(() => {
    if (!expiresAt) return
    const update = (): void =>
      setLeft(Math.max(0, Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 60_000)))
    update()
    const timer = window.setInterval(update, 30_000)
    return () => window.clearInterval(timer)
  }, [expiresAt])
  return left
}

/**
 * Two banners, one component:
 * - inside a support session, the operator is told it is read-only and when it ends;
 * - for the user being viewed and the account owner, that KAAMCO support is in
 *   their workspace right now, with a button to end it.
 */
export function SupportSessionBanner(): React.ReactElement | null {
  const support = useAuthStore((s) => s.user?.supportSession)
  return support ? <OperatorBanner /> : <TenantNotice />
}

function OperatorBanner(): React.ReactElement | null {
  const support = useAuthStore((s) => s.user?.supportSession)
  const user = useAuthStore((s) => s.user)
  const logout = useAuthStore((s) => s.logout)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const minutes = useMinutesLeft(support?.expiresAt)
  const end = useMutation({
    mutationFn: () => endSupportSession(support!.id),
    onSettled: () => {
      queryClient.clear()
      logout()
      navigate('/login', { replace: true })
    },
  })
  if (!support) return null

  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-amber-300 bg-amber-100 px-4 py-2 text-sm text-amber-950 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100"
    >
      <Eye className="h-4 w-4 shrink-0" aria-hidden />
      <span>
        <b>Read-only support session</b> as {user?.email} — opened by {support.operatorName}.
        {minutes !== null && <> Ends in {minutes} min.</>} Nothing can be changed.
      </span>
      <button
        type="button"
        onClick={() => end.mutate()}
        disabled={end.isPending}
        className="ml-auto rounded-md border border-amber-400 px-2.5 py-1 text-xs font-medium hover:bg-amber-200 dark:border-amber-600 dark:hover:bg-amber-900"
      >
        {end.isPending ? 'Ending…' : 'End session'}
      </button>
    </div>
  )
}

function TenantNotice(): React.ReactElement | null {
  const queryClient = useQueryClient()
  const { data } = useQuery({
    queryKey: VISIBLE_KEY,
    queryFn: getVisibleSupportSessions,
    // Sessions last thirty minutes; a minute's lag in noticing one is fine and
    // keeps this off the hot path.
    refetchInterval: 60_000,
    staleTime: 30_000,
  })
  const end = useMutation({
    mutationFn: endSupportSession,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: VISIBLE_KEY }),
  })
  const sessions = data ?? []
  if (sessions.length === 0) return null

  return (
    <div className="space-y-1 border-b border-sky-300 bg-sky-50 px-4 py-2 text-sm text-sky-950 dark:border-sky-800 dark:bg-sky-950 dark:text-sky-100">
      {sessions.map((session) => (
        <div key={session.id} role="status" className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <ShieldAlert className="h-4 w-4 shrink-0" aria-hidden />
          <span>
            <b>KAAMCO support</b> ({session.operatorName}) is viewing your workspace as{' '}
            {session.userEmail}, read-only, until{' '}
            {new Date(session.expiresAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}.
            <span className="text-sky-800 dark:text-sky-300"> Reason: “{session.reason}”</span>
          </span>
          <button
            type="button"
            onClick={() => end.mutate(session.id)}
            disabled={end.isPending}
            className="ml-auto rounded-md border border-sky-400 px-2.5 py-1 text-xs font-medium hover:bg-sky-100 dark:border-sky-700 dark:hover:bg-sky-900"
          >
            End it now
          </button>
        </div>
      ))}
    </div>
  )
}
